<template>
  <div class="promotion-task-page">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>推广任务管理</span>
          <el-button type="primary" @click="showCreateDialog">创建推广任务</el-button>
        </div>
      </template>

      <!-- 搜索栏 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="任务状态">
          <el-select v-model="searchForm.status" placeholder="全部" clearable>
            <el-option label="进行中" value="in_progress" />
            <el-option label="已完成" value="completed" />
            <el-option label="已过期" value="expired" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 数据表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="title" label="任务名称" min-width="150" />
        <el-table-column prop="reward" label="奖励金额" width="120">
          <template #default="{ row }">
            <span class="reward">¥{{ row.reward }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="targetCount" label="目标数量" width="100" />
        <el-table-column prop="completedCount" label="已完成" width="100" />
        <el-table-column prop="progress" label="进度" width="150">
          <template #default="{ row }">
            <el-progress 
              :percentage="getProgress(row)" 
              :status="getProgressStatus(row)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusName(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="deadline" label="截止时间" width="180" />
        <el-table-column label="操作" fixed="right" width="200">
          <template #default="{ row }">
            <el-button type="primary" link @click="viewDetails(row)">详情</el-button>
            <el-button type="success" link @click="viewRecords(row)">记录</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        class="pagination"
      />
    </el-card>

    <!-- 创建任务弹窗 -->
    <el-dialog v-model="createDialogVisible" title="创建推广任务" width="500px">
      <el-form :model="createForm" label-width="100px">
        <el-form-item label="任务名称">
          <el-input v-model="createForm.title" placeholder="请输入任务名称" />
        </el-form-item>
        <el-form-item label="奖励金额">
          <el-input-number v-model="createForm.reward" :min="1" :precision="2" />
        </el-form-item>
        <el-form-item label="目标数量">
          <el-input-number v-model="createForm.targetCount" :min="1" />
        </el-form-item>
        <el-form-item label="截止时间">
          <el-date-picker v-model="createForm.deadline" type="datetime" placeholder="选择截止时间" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="createTask">创建</el-button>
      </template>
    </el-dialog>

    <!-- 入驻记录弹窗 -->
    <el-dialog v-model="recordDialogVisible" title="商家入驻记录" width="800px">
      <el-table :data="recordList" v-loading="recordLoading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="merchantName" label="商家名称" width="150" />
        <el-table-column prop="referrerName" label="推广员" width="120" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 'approved' ? 'success' : 'warning'">
              {{ row.status === 'approved' ? '已通过' : row.status === 'pending' ? '待审核' : '已拒绝' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="入驻时间" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

const loading = ref(false)
const recordLoading = ref(false)
const tableData = ref([])
const recordList = ref([])
const createDialogVisible = ref(false)
const recordDialogVisible = ref(false)

const searchForm = reactive({
  status: ''
})

const createForm = reactive({
  title: '',
  reward: 100,
  targetCount: 5,
  deadline: null
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const getProgress = (row) => {
  if (!row.targetCount) return 0
  return Math.round((row.completedCount || 0) / row.targetCount * 100)
}

const getProgressStatus = (row) => {
  const progress = getProgress(row)
  if (progress >= 100) return 'success'
  if (progress >= 60) return 'warning'
  return ''
}

const getStatusType = (status) => {
  const types = {
    'in_progress': 'primary',
    'completed': 'success',
    'expired': 'info'
  }
  return types[status] || 'info'
}

const getStatusName = (status) => {
  const names = {
    'in_progress': '进行中',
    'completed': '已完成',
    'expired': '已过期'
  }
  return names[status] || status
}

const fetchData = async () => {
  loading.value = true
  try {
    const res = await axios.get('/api/tasks', {
      params: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        taskType: 'merchant_expansion',
        ...searchForm
      }
    })
    if (res.data.code === 200) {
      tableData.value = res.data.data.list || []
      pagination.total = res.data.data.total || 0
    }
  } catch (e) {
    // 模拟数据
    tableData.value = [
      { id: 1, title: '餐饮商家拓展', reward: 100, targetCount: 5, completedCount: 3, status: 'in_progress', deadline: '2026-03-25 23:59:59' },
      { id: 2, title: '零售商家拓展', reward: 80, targetCount: 10, completedCount: 10, status: 'completed', deadline: '2026-03-20 23:59:59' }
    ]
    pagination.total = 2
  } finally {
    loading.value = false
  }
}

const showCreateDialog = () => {
  createForm.title = ''
  createForm.reward = 100
  createForm.targetCount = 5
  createForm.deadline = null
  createDialogVisible.value = true
}

const createTask = async () => {
  if (!createForm.title) {
    ElMessage.warning('请输入任务名称')
    return
  }
  try {
    const res = await axios.post('/api/tasks/publish', {
      ...createForm,
      taskType: 'merchant_expansion',
      taskSource: 'platform'
    })
    if (res.data.code === 200) {
      ElMessage.success('创建成功')
      createDialogVisible.value = false
      fetchData()
    }
  } catch (e) {
    ElMessage.success('创建成功（模拟）')
    createDialogVisible.value = false
  }
}

const viewDetails = (row) => {
  ElMessage.info(`查看任务详情: ${row.title}`)
}

const viewRecords = async (row) => {
  recordDialogVisible.value = true
  recordLoading.value = true
  
  try {
    const res = await axios.get(`/api/promotion/admin/records/${row.id}`)
    if (res.data.code === 200) {
      recordList.value = res.data.data.list || []
    }
  } catch (e) {
    recordList.value = [
      { id: 1, merchantName: '美味餐厅', referrerName: '张三', status: 'approved', createdAt: '2026-03-20 10:00:00' },
      { id: 2, merchantName: '便民超市', referrerName: '李四', status: 'pending', createdAt: '2026-03-20 11:00:00' }
    ]
  } finally {
    recordLoading.value = false
  }
}

const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

const resetSearch = () => {
  searchForm.status = ''
  handleSearch()
}

const handleSizeChange = (val) => {
  pagination.pageSize = val
  fetchData()
}

const handleCurrentChange = (val) => {
  pagination.page = val
  fetchData()
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.promotion-task-page {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

.reward {
  color: #f56c6c;
  font-weight: bold;
}
</style>
